import { Calendar, Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { InterviewType } from "../../graphql/generated/graphql";
import { useSponsor, useTermMutations, useTerms } from "../../hooks";

// Record で持つことで、種別が増えたときにラベル追加漏れを型で検知する
const INTERVIEW_TYPE_LABELS: Record<InterviewType, string> = {
  [InterviewType.Employee]: "従業員インタビュー",
  [InterviewType.Stakeholder]: "ステークホルダーインタビュー",
};

export function SponsorDetail() {
  const navigate = useNavigate();
  const { sponsorId } = useParams<{ sponsorId: string }>();

  const {
    sponsor,
    loading: sponsorLoading,
    error: sponsorError,
  } = useSponsor(sponsorId ?? null);
  const {
    terms,
    loading: termsLoading,
    error: termsError,
    refetch: refetchTerms,
  } = useTerms(sponsorId ?? null);
  const { createTerm } = useTermMutations();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    notionUrl: string;
    durationMinutes: number;
    interviewType: InterviewType;
  }>({
    title: "",
    description: "",
    notionUrl: "",
    durationMinutes: 60,
    interviewType: InterviewType.Employee,
  });
  const [isCreating, setIsCreating] = useState(false);

  const loading = sponsorLoading || termsLoading;
  const error = sponsorError || termsError;

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("ja-JP");
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sponsorId || !formData.title.trim()) return;

    setIsCreating(true);
    const result = await createTerm({
      sponsorId,
      interviewType: formData.interviewType,
      title: formData.title.trim(),
      description: formData.description.trim() || undefined,
      notionUrl: formData.notionUrl.trim() || undefined,
      durationMinutes: formData.durationMinutes,
    });

    if (result) {
      setFormData({
        title: "",
        description: "",
        notionUrl: "",
        durationMinutes: 60,
        interviewType: InterviewType.Employee,
      });
      setShowCreateDialog(false);
      refetchTerms();
    }
    setIsCreating(false);
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[#ef4444]">
          {error}
        </div>
      </div>
    );
  }

  if (!sponsor) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[#ef4444]">
          スポンサーが見つかりません
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
      <div className="w-full">
        {/* パンくずリスト */}
        <div className="mb-4">
          <nav className="flex items-center gap-2 text-[13px] text-[var(--color-text-secondary)]">
            <button
              type="button"
              onClick={() => navigate("/employee_interview/sponsors")}
              className="cursor-pointer border-none bg-none p-0 text-[var(--color-primary-500)] text-inherit no-underline hover:underline"
            >
              スポンサー一覧
            </button>
            <span className="text-[#9ca3af]">/</span>
            <span className="font-medium text-[var(--color-text-primary)]">
              {sponsor.name}
            </span>
          </nav>
        </div>

        {/* ヘッダーセクション */}
        <div className="mb-8 flex items-start justify-between">
          <div className="border-[var(--color-primary-500)] border-l-4 pl-5">
            <h1 className="mb-2 font-bold text-[32px] text-[var(--color-primary-500)]">
              {sponsor.name}
            </h1>
            {sponsor.description && (
              <p className="font-medium text-[var(--color-text-secondary)] text-sm">
                {sponsor.description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowCreateDialog(true)}
            className="flex cursor-pointer items-center gap-1.5 rounded-md border-none bg-[var(--color-primary-500)] px-5 py-2.5 font-medium text-sm text-white hover:bg-[#045a94]"
          >
            <Plus className="h-4 w-4" />
            新規ターム作成
          </button>
        </div>

        {/* ターム一覧カード */}
        <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
            <div>
              <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
                ターム一覧
              </h2>
              <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
                {terms.length}件のタームが登録されています
              </p>
            </div>
          </div>
          <div className="p-0">
            {terms.length === 0 ? (
              <div className="p-12 text-center">
                <Calendar className="mx-auto mb-4 h-12 w-12 text-[#9ca3af]" />
                <p className="text-[var(--color-text-secondary)]">
                  タームがありません
                </p>
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[var(--color-bg-subtle-gray)]">
                    <th className="border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      種別
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      インタビュータイトル
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      説明
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      作成日
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {terms.map((term) => (
                    <tr
                      key={term.termId}
                      onClick={() =>
                        navigate(`/employee_interview/terms/${term.termId}`)
                      }
                      className="cursor-pointer border-[var(--color-border-default)] border-b hover:bg-[var(--color-bg-subtle-gray)]"
                    >
                      <td className="px-6 py-4 text-sm">
                        <span className="whitespace-nowrap rounded-full bg-[var(--color-bg-subtle-gray)] px-2.5 py-1 font-medium text-[12px] text-[var(--color-text-secondary)]">
                          {INTERVIEW_TYPE_LABELS[term.interviewType]}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-medium text-[var(--color-primary-500)] text-sm">
                        {term.title}
                      </td>
                      <td className="px-6 py-4 text-[var(--color-text-secondary)] text-sm">
                        {term.description || "-"}
                      </td>
                      <td className="px-6 py-4 text-[var(--color-text-secondary)] text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {formatDate(term.createdAt)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* 作成ダイアログ */}
        {showCreateDialog && (
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50"
            onClick={() => setShowCreateDialog(false)}
          >
            <div
              className="w-full max-w-[450px] rounded-xl bg-white p-6 shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="mb-5 font-semibold text-[var(--color-text-primary)] text-lg">
                タームを作成
              </h3>
              <form onSubmit={handleCreate}>
                <div className="mb-4">
                  <span className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm">
                    種別
                  </span>
                  <div className="flex gap-4">
                    {Object.entries(INTERVIEW_TYPE_LABELS).map(
                      ([interviewType, label]) => (
                        <label
                          key={interviewType}
                          className="flex cursor-pointer items-center gap-1.5"
                        >
                          <input
                            type="radio"
                            name="interviewType"
                            value={interviewType}
                            checked={formData.interviewType === interviewType}
                            onChange={() =>
                              setFormData({
                                ...formData,
                                interviewType: interviewType as InterviewType,
                              })
                            }
                            className="accent-[var(--color-primary-500)]"
                          />
                          <span className="text-sm">{label}</span>
                        </label>
                      ),
                    )}
                  </div>
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="title"
                    className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm"
                  >
                    インタビュータイトル
                  </label>
                  <input
                    id="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="例: 2024年第1四半期"
                    required
                    className="box-border w-full rounded-md border border-[var(--color-border-default)] px-3 py-2.5 text-sm outline-none"
                  />
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="description"
                    className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm"
                  >
                    詳細（任意）
                  </label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="ターム詳細を入力"
                    rows={3}
                    className="box-border w-full resize-y rounded-md border border-[var(--color-border-default)] px-3 py-2.5 text-sm outline-none"
                  />
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="notionUrl"
                    className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm"
                  >
                    Notion案件ページURL（任意）
                  </label>
                  <input
                    id="notionUrl"
                    type="url"
                    value={formData.notionUrl}
                    onChange={(e) =>
                      setFormData({ ...formData, notionUrl: e.target.value })
                    }
                    placeholder="https://www.notion.so/..."
                    className="box-border w-full rounded-md border border-[var(--color-border-default)] px-3 py-2.5 text-sm outline-none"
                  />
                </div>
                <div className="mb-5">
                  <span className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm">
                    インタビュー所要時間
                  </span>
                  <div className="flex gap-4">
                    {[30, 45, 60].map((minutes) => (
                      <label
                        key={minutes}
                        className="flex cursor-pointer items-center gap-1.5"
                      >
                        <input
                          type="radio"
                          name="durationMinutes"
                          value={minutes}
                          checked={formData.durationMinutes === minutes}
                          onChange={() =>
                            setFormData({
                              ...formData,
                              durationMinutes: minutes,
                            })
                          }
                          className="accent-[var(--color-primary-500)]"
                        />
                        <span className="text-sm">{minutes}分</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateDialog(false)}
                    disabled={isCreating}
                    className={`rounded-md border border-[var(--color-text-secondary)] bg-white px-4 py-2.5 font-medium text-[var(--color-text-secondary)] text-sm ${isCreating ? "cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating || !formData.title.trim()}
                    className={`rounded-md border-none px-4 py-2.5 font-medium text-sm text-white ${
                      isCreating || !formData.title.trim()
                        ? "cursor-not-allowed bg-[#9ca3af]"
                        : "cursor-pointer bg-[var(--color-primary-500)]"
                    }`}
                  >
                    {isCreating ? "作成中..." : "作成"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
